import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import HotelDetailPage from "@/pages/HotelDetailPage";
import DestinationDetailPage from "@/pages/DestinationDetailPage";
import AirlineDetailPage from "@/pages/AirlineDetailPage";
import AdminDashboard from "@/pages/AdminDashboard";
import BadgeEmbedPage from "@/pages/BadgeEmbedPage";
import SignInPage from "@/pages/SignInPage";
import UserDashboardPage from "@/pages/UserDashboardPage";
import MethodologyPage from "@/pages/MethodologyPage";
import DestinationMethodologyPage from "@/pages/DestinationMethodologyPage";
import QuestionnairePage from "@/pages/QuestionnairePage";
import AlliancePage from "@/pages/AlliancePage";
import { AuthProvider } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [hotels, setHotels] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [hotelsRes, destinationsRes, airlinesRes, statsRes] = await Promise.all([
        axios.get(`${API}/hotels`),
        axios.get(`${API}/destinations`),
        axios.get(`${API}/airlines`),
        axios.get(`${API}/stats`)
      ]);
      setHotels(hotelsRes.data);
      setDestinations(destinationsRes.data);
      setAirlines(airlinesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      // If no data, seed it
      try {
        await axios.post(`${API}/seed`);
        const [hotelsRes, destinationsRes, airlinesRes, statsRes] = await Promise.all([
          axios.get(`${API}/hotels`),
          axios.get(`${API}/destinations`),
          axios.get(`${API}/airlines`),
          axios.get(`${API}/stats`)
        ]);
        setHotels(hotelsRes.data);
        setDestinations(destinationsRes.data);
        setAirlines(airlinesRes.data);
        setStats(statsRes.data);
      } catch (seedError) {
        console.error("Error seeding data:", seedError);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filterHotels = async (rating, search) => {
    try {
      const params = new URLSearchParams();
      if (rating && rating !== "all") params.append("rating", rating);
      if (search) params.append("search", search);
      const response = await axios.get(`${API}/hotels?${params.toString()}`);
      setHotels(response.data);
    } catch (error) {
      console.error("Error filtering hotels:", error);
    }
  };

  const filterDestinations = async (rating, search) => {
    try {
      const params = new URLSearchParams();
      if (rating && rating !== "all") params.append("rating", rating);
      if (search) params.append("search", search);
      const response = await axios.get(`${API}/destinations?${params.toString()}`);
      setDestinations(response.data);
    } catch (error) {
      console.error("Error filtering destinations:", error);
    }
  };

  const filterAirlines = async (rating, search) => {
    try {
      const params = new URLSearchParams();
      if (rating && rating !== "all") params.append("rating", rating);
      if (search) params.append("search", search);
      const response = await axios.get(`${API}/airlines?${params.toString()}`);
      setAirlines(response.data);
    } catch (error) {
      console.error("Error filtering airlines:", error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route 
              path="/" 
              element={
                <HomePage 
                  hotels={hotels}
                  destinations={destinations}
                  airlines={airlines}
                  stats={stats}
                  loading={loading}
                  onFilterHotels={filterHotels}
                  onFilterDestinations={filterDestinations}
                  onFilterAirlines={filterAirlines}
                />
              } 
            />
            <Route path="/hotel/:id" element={<HotelDetailPage />} />
            <Route path="/destination/:id" element={<DestinationDetailPage />} />
            <Route path="/airline/:id" element={<AirlineDetailPage />} />
            <Route path="/embed/:type/:id" element={<BadgeEmbedPage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/dashboard" element={<UserDashboardPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/methodology/destinations" element={<DestinationMethodologyPage />} />
            <Route path="/questionnaire" element={<QuestionnairePage />} />
            <Route path="/alliance" element={<AlliancePage />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
